import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileText, Search } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const getDocumentStatus = (expiryDate) => {
  if (!expiryDate) return "No Expiry";

  const today = new Date();
  const expiry = new Date(expiryDate);

  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);

  const diffInMs = expiry - today;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) return "Expired";
  if (diffInDays <= 30) return "Expiring Soon";
  return "Valid";
};

const formatDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusClasses = {
  Valid: "bg-emerald-100 text-emerald-700",
  "Expiring Soon": "bg-amber-100 text-amber-700",
  Expired: "bg-rose-100 text-rose-700",
  "No Expiry": "bg-slate-100 text-slate-700",
};

export default function DocumentsPage() {
  const token = localStorage.getItem("staff_portal_token");

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-get-documents",
      token,
      data: {},
    });

    if (!res?.success) {
      setError(res?.error || "Failed to fetch documents");
      setLoading(false);
      return;
    }

    const mappedDocuments = (res.documents || []).map((doc) => ({
      ...doc,
      status: getDocumentStatus(doc.expiry_date),
    }));

    setDocuments(mappedDocuments);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const filteredDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return documents.filter((doc) => {
      const matchesSearch =
        !query ||
        String(doc.staff_name || "").toLowerCase().includes(query) ||
        String(doc.staff_id || "").toLowerCase().includes(query) ||
        String(doc.file_name || "").toLowerCase().includes(query);

      const matchesType =
        !documentTypeFilter || doc.document_type === documentTypeFilter;

      const matchesStatus = !statusFilter || doc.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [documents, search, documentTypeFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: documents.length,
      expiringSoon: documents.filter(
        (doc) => doc.status === "Expiring Soon",
      ).length,
      expired: documents.filter((doc) => doc.status === "Expired").length,
    };
  }, [documents]);

  const documentTypes = useMemo(() => {
    return [
      ...new Set(documents.map((doc) => doc.document_type).filter(Boolean)),
    ];
  }, [documents]);

const handleViewDocument = (docId) => {
  if (!docId || !token) return;

  const form = document.createElement("form");
  form.method = "POST";
  form.action = "https://staff.deedelicious.com/php/proxy.php";
  form.target = "_blank";
  form.style.display = "none";

  const fields = {
    script: "admin-view-document",
    api_token: token,
    id: String(docId),
  };

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDocs, setModalDocs] = useState([]);

  const handleShowDocs = (status) => {
    const docs = documents.filter((doc) => doc.status === status);
    setModalDocs(docs);
    setModalTitle(status === "Expired" ? "Expired Documents" : "Documents Expiring Soon");
    setShowStatusModal(true);
  };

  return (
    <AdminLayout title="Documents">
      <FullPageSpinner show={loading} text="Loading documents..." />

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Documents</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
                  {stats.total}
                </h3>
              </div>
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-slate-100">
                <FileText size={20} className="text-slate-700" />
              </div>
            </div>
          </div>

          <div
            onClick={() => handleShowDocs("Expiring Soon")}
            className="cursor-pointer rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Expiring Soon</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
                  {stats.expiringSoon}
                </h3>
              </div>
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-amber-200">
                <AlertTriangle size={20} className="text-amber-700" />
              </div>
            </div>
          </div>

          <div
            onClick={() => handleShowDocs("Expired")}
            className="col-span-2 md:col-span-1 cursor-pointer rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 p-4 sm:p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-600 font-medium">Expired</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
                  {stats.expired}
                </h3>
              </div>
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-rose-200">
                <AlertTriangle size={20} className="text-rose-700" />
              </div>
            </div>
          </div>
        </div>

        {showStatusModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
            <div className="w-full max-w-4xl max-h-[80vh] overflow-hidden rounded-3xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-100 p-6">
                <h3 className="text-xl font-bold text-slate-900">{modalTitle}</h3>
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="rounded-full p-2 text-2xl leading-none text-slate-500 hover:bg-slate-100"
                >
                  ✕
                </button>
              </div>
              
              <div className="overflow-auto p-6">
                <table className="w-full text-left">
                  <thead className="border-b border-slate-100">
                    <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-4 pr-4">Staff</th>
                      <th className="pb-4 pr-4">Document Type</th>
                      <th className="pb-4 pr-4">Expiry Date</th>
                      <th className="pb-4">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {modalDocs.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="py-10 text-center text-sm text-slate-500">
                          No documents found for this status.
                        </td>
                      </tr>
                    ) : (
                      modalDocs.map((doc) => (
                        <tr key={doc.id} className="text-sm">
                          <td className="py-4 pr-4">
                            <p className="font-medium text-slate-900">{doc.staff_name}</p>
                            <p className="text-xs text-slate-500">{doc.staff_id}</p>
                          </td>
                          <td className="py-4 pr-4 text-slate-700">{doc.document_type}</td>
                          <td className="py-4 pr-4 text-slate-700">{formatDate(doc.expiry_date)}</td>
                          <td className="py-4">
                            <button
                              onClick={() => {
                                setShowStatusModal(false);
                                handleViewDocument(doc.id);
                              }}
                              className="text-blue-600 font-semibold hover:underline"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-center">
              <div className="relative w-full md:max-w-sm">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by staff name, ID or file name..."
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 text-base outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div className="flex w-full flex-wrap gap-4 md:w-auto md:flex-nowrap">
                <select
                  value={documentTypeFilter}
                  onChange={(e) => setDocumentTypeFilter(e.target.value)}
                  className="min-w-[150px] flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-base outline-none focus:ring-2 focus:ring-slate-900 md:flex-none md:w-auto"
                >
                  <option value="">All Document Types</option>
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="min-w-[130px] flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-base outline-none focus:ring-2 focus:ring-slate-900 md:flex-none md:w-auto"
                >
                  <option value="">All Statuses</option>
                  <option value="Valid">Valid</option>
                  <option value="Expiring Soon">Expiring Soon</option>
                  <option value="Expired">Expired</option>
                  <option value="No Expiry">No Expiry</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px]">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Document Type
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Expiry Date
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      Loading documents...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-red-600"
                    >
                      {error}
                    </td>
                  </tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No documents found.
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      className="border-b border-slate-100 last:border-b-0"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {doc.staff_name}
                          </p>
                          <p className="text-xs text-slate-500">{doc.staff_id}</p>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {doc.document_type || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatDate(doc.expiry_date)}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            statusClasses[doc.status]
                          }`}
                        >
                          {doc.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(doc.id)}
                          disabled={!doc.id}
                          className={`text-sm font-medium ${
                            doc.id
                              ? "text-slate-900 hover:underline"
                              : "cursor-not-allowed text-slate-400"
                          }`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}